import { useState } from 'react';
import { displayObject } from '../../animation/timeline';
import * as A from '../../domain/animationOps';
import * as S from '../../domain/sceneOps';
import type { Animation, SceneObject, Scene } from '../../domain/schemas';
import type { AnimationSnapshot } from '../../animation/animationController';
import { Observable } from '../../lib/store';
import { useObservable, useObservableSelector } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Button, Panel } from '../ui';
import { EffectsEditor, FillEditor } from './LookControls';
import type { TransformEditFn } from './useAnimationEditing';
import type { EditFn } from './useSceneEdit';

const NO_FRAMES = new Observable<AnimationSnapshot>({ status: 'idle', frame: 0, elapsed: 0 });
const num = 'w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm aria-[invalid=true]:border-red-500';
const sel = 'w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';

const SHAPE_POINTS: Record<string, [number, number][]> = {
  Triangle: S.TRIANGLE,
  Diamond: [
    [0.5, 0],
    [1, 0.5],
    [0.5, 1],
    [0, 0.5],
  ],
  Pentagon: Array.from({ length: 5 }, (_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    return [Math.round((0.5 + 0.5 * Math.cos(a)) * 1000) / 1000, Math.round((0.5 + 0.5 * Math.sin(a)) * 1000) / 1000] as [number, number];
  }),
  Star: Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? 0.5 : 0.2;
    return [Math.round((0.5 + r * Math.cos(a)) * 1000) / 1000, Math.round((0.5 + r * Math.sin(a)) * 1000) / 1000] as [number, number];
  }),
};

/** Number input that only commits valid numbers and flags invalid ones. */
function NumberField({ id, label, value, onCommit, step = 1, min, max }: { id: string; label: string; value: number; onCommit: (v: number) => void; step?: number; min?: number; max?: number }) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? String(Math.round(value * 100) / 100);
  const invalid = draft !== null && (draft.trim() === '' || !Number.isFinite(Number(draft)));
  return (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className="text-[11px] text-zinc-400">
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        max={max}
        value={shown}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          const v = Number(e.target.value);
          if (e.target.value.trim() !== '' && Number.isFinite(v)) onCommit(v);
        }}
        onBlur={() => setDraft(null)}
        className={num}
      />
    </div>
  );
}

/** A box scaled about its anchor (used by the Larger / Smaller buttons). */
function scaled(o: SceneObject, factor: number): A.TransformEdit {
  const width = Math.min(8192, Math.max(1, o.width * factor));
  const height = Math.min(8192, Math.max(1, o.height * factor));
  return { x: o.x - (width - o.width) * o.anchorX, y: o.y - (height - o.height) * o.anchorY, width, height };
}

function ColorField({ id, label, value, onCommit }: { id: string; label: string; value: string; onCommit: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input id={id} type="color" value={value} onChange={(e) => onCommit(e.target.value)} className="h-8 w-10 cursor-pointer rounded border border-zinc-700 bg-transparent" />
      <label htmlFor={id} className="text-xs text-zinc-300">
        {label} <span className="font-mono text-zinc-400">{value}</span>
      </label>
    </div>
  );
}

/**
 * Precise numeric/property editing for the selection, plus touch-friendly transform
 * buttons. Transform edits go through `onTransform`: with Auto-Key on, the transform
 * fields show (and key) the values at the playhead; otherwise they show and edit the base.
 */
export function ObjectInspector({
  scene,
  edit,
  onTransform,
  animation = null,
  frames,
  error,
}: {
  scene: Scene;
  edit: EditFn;
  onTransform: TransformEditFn;
  animation?: Animation | null;
  /** Renderer snapshots; the displayed frame is only followed while Auto-Key is on. */
  frames?: Observable<AnimationSnapshot>;
  error: string | null;
}) {
  const { selection, animation: bus } = useServices();
  const selected = useObservable(selection);
  const autoKey = useObservable(bus.autoKey) && !!animation;
  const frame = useObservableSelector(frames ?? NO_FRAMES, (v) => (autoKey ? v.frame : 0));
  const objects = scene.objects.filter((o) => selected.objectIds.includes(o.id));
  const sid = scene.id;
  const ids = objects.map((o) => o.id);
  const editable = objects.filter((o) => !S.isLocked(scene, o)).map((o) => o.id);
  const anyLocked = editable.length < ids.length;

  const quick = (
    <div className="flex flex-col gap-2" aria-label="Quick transform">
      <div className="grid grid-cols-3 gap-1" role="group" aria-label="Move selection">
        <span />
        <Button size="sm" onClick={() => onTransform('Move', editable, (o) => ({ y: o.y - 10 }))} disabled={!editable.length} aria-label="Move up 10 pixels">
          ↑
        </Button>
        <span />
        <Button size="sm" onClick={() => onTransform('Move', editable, (o) => ({ x: o.x - 10 }))} disabled={!editable.length} aria-label="Move left 10 pixels">
          ←
        </Button>
        <Button size="sm" onClick={() => onTransform('Move', editable, (o) => ({ y: o.y + 10 }))} disabled={!editable.length} aria-label="Move down 10 pixels">
          ↓
        </Button>
        <Button size="sm" onClick={() => onTransform('Move', editable, (o) => ({ x: o.x + 10 }))} disabled={!editable.length} aria-label="Move right 10 pixels">
          →
        </Button>
      </div>
      <div className="flex flex-wrap gap-1">
        <Button size="sm" onClick={() => onTransform('Resize', editable, (o) => scaled(o, 1.1))} disabled={!editable.length} data-testid="scale-up">
          Larger
        </Button>
        <Button size="sm" onClick={() => onTransform('Resize', editable, (o) => scaled(o, 1 / 1.1))} disabled={!editable.length}>
          Smaller
        </Button>
        <Button size="sm" onClick={() => onTransform('Rotate', editable, (o) => ({ rotation: o.rotation - 15 }))} disabled={!editable.length} aria-label="Rotate 15 degrees left">
          ⟲ 15°
        </Button>
        <Button size="sm" onClick={() => onTransform('Rotate', editable, (o) => ({ rotation: o.rotation + 15 }))} disabled={!editable.length} aria-label="Rotate 15 degrees right">
          ⟳ 15°
        </Button>
      </div>
    </div>
  );

  if (objects.length === 0) {
    return (
      <Panel title="Inspector" headingLevel={3}>
        <p className="text-sm text-zinc-400">Select an object on the canvas or in the layer list. Tip: Tab cycles through objects; drag on empty space to select several.</p>
        {error && <p role="alert" className="mt-2 text-xs text-red-300">{error}</p>}
      </Panel>
    );
  }

  const o: SceneObject | null = objects.length === 1 ? objects[0]! : null;
  const set = (label: string, patch: S.ObjectPatch, key: string) => edit(label, (p) => S.updateObjects(p, sid, ids, patch), `${ids.join(',')}:${key}`);
  const f = (k: string) => `insp-${k}`;
  // Transform fields: values at the playhead while Auto-Key is on, base values otherwise.
  const t = o && autoKey && animation ? displayObject(o, A.valuesAt(animation, o.id, frame)) : o;
  const setT = (label: string, wanted: A.TransformEdit, key: string) => o && onTransform(label, [o.id], () => wanted, `${o.id}:${key}${autoKey ? `@${frame}` : ''}`, 'base');
  const animated = o && animation ? A.animatedChannels(animation, o.id) : [];

  return (
    <Panel title={o ? `Inspector · ${o.name}` : `Inspector · ${objects.length} objects`} headingLevel={3}>
      <div className="flex flex-col gap-3" data-testid="inspector">
        {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
        {anyLocked && <p className="text-xs text-amber-300">{o ? 'This object is locked (or on a locked layer). Unlock it to edit.' : 'Locked objects in the selection are not changed.'}</p>}
        <div className="flex flex-wrap gap-1">
          <Button
            size="sm"
            onClick={() => {
              let created: string[] = [];
              if (
                edit('Duplicate', (p) => {
                  const r = S.duplicateObjects(p, sid, editable);
                  created = r.ids;
                  return r.project;
                })
              )
                selection.selectObjects(created);
            }}
            disabled={!editable.length}
          >
            Duplicate
          </Button>
          <Button size="sm" variant="danger" onClick={() => edit('Delete', (p) => S.deleteObjects(p, sid, editable)) && selection.selectObjects([])} disabled={!editable.length}>
            Delete
          </Button>
          <Button size="sm" onClick={() => edit('Bring to front', (p) => S.reorderObjects(p, sid, editable, 'front'))} disabled={!editable.length}>
            To front
          </Button>
          <Button size="sm" onClick={() => edit('Bring forward', (p) => S.reorderObjects(p, sid, editable, 'forward'))} disabled={!editable.length}>
            Forward
          </Button>
          <Button size="sm" onClick={() => edit('Send backward', (p) => S.reorderObjects(p, sid, editable, 'backward'))} disabled={!editable.length}>
            Backward
          </Button>
          <Button size="sm" onClick={() => edit('Send to back', (p) => S.reorderObjects(p, sid, editable, 'back'))} disabled={!editable.length}>
            To back
          </Button>
          {objects.length > 1 && (
            <Button size="sm" onClick={() => edit('Group', (p) => S.groupObjects(p, sid, editable))} disabled={editable.length < 2}>
              Group
            </Button>
          )}
          {objects.some((x) => x.groupId) && (
            <Button size="sm" onClick={() => edit('Ungroup', (p) => S.ungroupObjects(p, sid, editable))} disabled={!editable.length}>
              Ungroup
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={objects.every((x) => x.visible)} disabled={anyLocked} onChange={(e) => edit(e.target.checked ? 'Show' : 'Hide', (p) => S.setVisible(p, sid, ids, e.target.checked))} />
            Visible
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={objects.every((x) => x.locked)} onChange={(e) => edit(e.target.checked ? 'Lock' : 'Unlock', (p) => S.setLocked(p, sid, ids, e.target.checked))} data-testid="insp-locked" />
            Locked
          </label>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={f('layer')} className="text-[11px] text-zinc-400">
            Layer
          </label>
          <select
            id={f('layer')}
            className={sel}
            value={objects.every((x) => x.layerId === objects[0]!.layerId) ? objects[0]!.layerId : ''}
            disabled={!editable.length}
            onChange={(e) => e.target.value && edit('Move to layer', (p) => S.moveObjectsToLayer(p, sid, editable, e.target.value))}
          >
            <option value="" disabled>
              (mixed)
            </option>
            {[...scene.layers].reverse().map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.locked ? ' (locked)' : ''}
              </option>
            ))}
          </select>
        </div>
        {quick}
        {o && animation && (
          <p className="text-xs text-zinc-400" data-testid="insp-anim-note">
            {autoKey ? (
              <>
                <span className="font-semibold text-red-300">Auto-Key</span> · transform edits key “{animation.name}” at frame {frame}.
              </>
            ) : animated.length ? (
              <>Base values. “{animation.name}” animates {animated.map((c) => A.CHANNEL_LABELS[c]).join(', ')}; turn on Auto-Key to edit keyframes.</>
            ) : (
              <>Base values (not animated in “{animation.name}”).</>
            )}
          </p>
        )}
        {o && t && (
          <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-3" disabled={anyLocked}>
            <legend className="sr-only">Transform</legend>
            <div className="col-span-2 flex flex-col gap-0.5 sm:col-span-3">
              <label htmlFor={f('name')} className="text-[11px] text-zinc-400">
                Name
              </label>
              <input
                id={f('name')}
                key={o.id}
                defaultValue={o.name}
                maxLength={60}
                className={num}
                onBlur={(e) => e.target.value.trim() && e.target.value !== o.name && edit('Rename', (p) => S.renameObject(p, sid, o.id, e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              />
            </div>
            <NumberField id={f('x')} label="X" value={t.x} onCommit={(v) => setT('Move', { x: v }, 'x')} />
            <NumberField id={f('y')} label="Y" value={t.y} onCommit={(v) => setT('Move', { y: v }, 'y')} />
            <NumberField id={f('rot')} label="Rotation °" value={t.rotation} onCommit={(v) => setT('Rotate', { rotation: v }, 'rotation')} />
            <NumberField id={f('w')} label="Width" value={t.width} min={1} onCommit={(v) => setT('Resize', { width: v }, 'width')} />
            <NumberField id={f('h')} label="Height" value={t.height} min={1} onCommit={(v) => setT('Resize', { height: v }, 'height')} />
            <NumberField id={f('op')} label="Opacity %" value={t.opacity * 100} min={0} max={100} step={5} onCommit={(v) => setT('Opacity', { opacity: v / 100 }, 'opacity')} />
            <NumberField id={f('ax')} label="Anchor X %" value={o.anchorX * 100} min={0} max={100} step={10} onCommit={(v) => set('Anchor', { anchorX: v / 100 }, 'anchorX')} />
            <NumberField id={f('ay')} label="Anchor Y %" value={o.anchorY * 100} min={0} max={100} step={10} onCommit={(v) => set('Anchor', { anchorY: v / 100 }, 'anchorY')} />
          </fieldset>
        )}
        {o && 'style' in o && (
          <fieldset className="flex flex-col gap-2" disabled={anyLocked}>
            <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Style</legend>
            <ColorField id={f('fill')} label={o.style.gradient ? 'Base colour' : 'Fill'} value={o.style.fill} onCommit={(v) => set('Colour', { style: { fill: v } } as S.ObjectPatch, 'fill')} />
            <FillEditor object={o} edit={edit} sceneId={sid} />
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={!!o.style.stroke}
                onChange={(e) => set('Outline', { style: { stroke: e.target.checked ? { color: '#ffffff', width: 4 } : null } } as S.ObjectPatch, 'stroke')}
              />
              Outline
            </label>
            {o.style.stroke && (
              <div className="grid grid-cols-2 items-end gap-2">
                <ColorField id={f('stroke')} label="Outline" value={o.style.stroke.color} onCommit={(v) => set('Outline', { style: { stroke: { ...o.style.stroke!, color: v } } } as S.ObjectPatch, 'strokeColor')} />
                <NumberField id={f('sw')} label="Outline width" value={o.style.stroke.width} min={0} max={64} onCommit={(v) => set('Outline', { style: { stroke: { ...o.style.stroke!, width: Math.min(64, Math.max(0, v)) } } } as S.ObjectPatch, 'strokeWidth')} />
              </div>
            )}
            {o.type === 'rect' && <NumberField id={f('radius')} label="Corner radius" value={o.cornerRadius} min={0} onCommit={(v) => set('Corners', { cornerRadius: Math.max(0, Math.min(1024, v)) } as S.ObjectPatch, 'radius')} />}
            {o.type === 'polygon' && (
              <div className="flex flex-wrap gap-1" role="group" aria-label="Polygon shape">
                {Object.entries(SHAPE_POINTS).map(([name, points]) => (
                  <Button key={name} size="sm" onClick={() => set('Shape', { points } as S.ObjectPatch, 'points')}>
                    {name}
                  </Button>
                ))}
              </div>
            )}
          </fieldset>
        )}
        {o?.type === 'text' && (
          <fieldset className="flex flex-col gap-2" disabled={anyLocked}>
            <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Text</legend>
            <label htmlFor={f('text')} className="sr-only">
              Text content
            </label>
            <textarea
              id={f('text')}
              key={o.id}
              defaultValue={o.text}
              maxLength={500}
              rows={2}
              className={num}
              onChange={(e) => e.target.value.length > 0 && set('Edit text', { text: e.target.value } as S.ObjectPatch, 'text')}
            />
            <ColorField id={f('tcolor')} label="Colour" value={o.color} onCommit={(v) => set('Colour', { color: v } as S.ObjectPatch, 'color')} />
            <div className="grid grid-cols-3 gap-2">
              <NumberField id={f('fs')} label="Size" value={o.fontSize} min={6} max={512} onCommit={(v) => set('Font size', { fontSize: Math.min(512, Math.max(6, v)) } as S.ObjectPatch, 'fontSize')} />
              <div className="flex flex-col gap-0.5">
                <label htmlFor={f('ff')} className="text-[11px] text-zinc-400">
                  Font
                </label>
                <select id={f('ff')} className={sel} value={o.fontFamily} onChange={(e) => set('Font', { fontFamily: e.target.value } as S.ObjectPatch, 'font')}>
                  <option value="sans">Sans</option>
                  <option value="serif">Serif</option>
                  <option value="mono">Mono</option>
                </select>
              </div>
              <div className="flex flex-col gap-0.5">
                <label htmlFor={f('al')} className="text-[11px] text-zinc-400">
                  Align
                </label>
                <select id={f('al')} className={sel} value={o.align} onChange={(e) => set('Align', { align: e.target.value } as S.ObjectPatch, 'align')}>
                  <option value="left">Left</option>
                  <option value="center">Centre</option>
                  <option value="right">Right</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={o.bold} onChange={(e) => set('Bold', { bold: e.target.checked } as S.ObjectPatch, 'bold')} />
              Bold
            </label>
          </fieldset>
        )}
        {o?.type === 'image' && (
          <div className="flex flex-col gap-0.5">
            <label htmlFor={f('fit')} className="text-[11px] text-zinc-400">
              Image fit
            </label>
            <select id={f('fit')} className={sel} value={o.fit} disabled={anyLocked} onChange={(e) => set('Image fit', { fit: e.target.value } as S.ObjectPatch, 'fit')}>
              <option value="stretch">Stretch</option>
              <option value="contain">Contain</option>
              <option value="cover">Cover (crop)</option>
            </select>
          </div>
        )}
        {editable.length > 0 && !anyLocked && <EffectsEditor objects={objects} ids={ids} edit={edit} sceneId={sid} />}
        {o?.type === 'character' && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={o.flipX} disabled={anyLocked} onChange={(e) => set('Flip', { flipX: e.target.checked } as S.ObjectPatch, 'flip')} />
              Flip horizontally
            </label>
            <Button size="sm" onClick={() => selection.selectCharacter(o.characterId)}>
              Edit character
            </Button>
            <span className="text-xs text-zinc-400">Size and position belong to this instance; looks belong to the character.</span>
          </div>
        )}
      </div>
    </Panel>
  );
}
